import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './SettingsPageNew.css';

const SETTINGS_TABS = [
  { id: 'general', label: 'General', path: '/settings' },
  { id: 'account', label: 'Account', path: '/settings/account' },
  { id: 'privacy', label: 'Privacy', path: '/settings/privacy' },
  { id: 'billing', label: 'Billing', path: '/settings/billing' },
  { id: 'delete', label: 'Delete Account', path: '/settings/delete-account' }
];

function SettingsPageNew() {
  const context = useOutletContext();
  const {
    user,
    onLogout,
    hasActiveSubscription,
    subscriptionTier,
    monthlyChecksUsed,
    monthlyCheckLimit,
    checksResetDate,
    sidebarOpen,
    onToggleSidebar
  } = context;

  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/settings' || path === '/settings/') return 'general';
    if (path.includes('/account')) return 'account';
    if (path.includes('/privacy')) return 'privacy';
    if (path.includes('/billing')) return 'billing';
    if (path.includes('/delete')) return 'delete';
    return 'general';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    practice_name: '',
    role: ''
  });
  const [subscription, setSubscription] = useState({
    status: 'free',
    customerId: null,
    periodEnd: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
    }

    if (data) {
      setProfile({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        practice_name: data.practice_name || '',
        role: data.role || ''
      });
      setSubscription({
        status: data.subscription_status || 'free',
        customerId: data.stripe_customer_id || null,
        periodEnd: data.subscription_period_end || null
      });
    }

    setLoading(false);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setSaving(true);

    const { data: existing } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    let result;
    if (existing) {
      result = await supabase
        .from('profiles')
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          practice_name: profile.practice_name,
          role: profile.role,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
    } else {
      result = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          practice_name: profile.practice_name,
          role: profile.role
        });
    }

    setSaving(false);

    if (result.error) {
      setError('Failed to save profile');
      return;
    }

    setSuccessMessage('Profile saved successfully');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setPasswordLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    setPasswordLoading(false);

    if (error) {
      setError(error.message);
      return;
    }

    setSuccessMessage('Password updated successfully');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowPasswordForm(false);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleManageSubscription = async () => {
    if (!subscription.customerId) return;

    setPortalLoading(true);
    setError('');

    try {
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: subscription.customerId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setError('');

    try {
      const response = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }

      await supabase.auth.signOut();
      onLogout();
      navigate('/');
    } catch (err) {
      console.error('Account deletion error:', err);
      setError('Failed to delete account');
      setShowDeleteConfirm(false);
    }
  };

  const getSubscriptionDisplay = () => {
    const tierName = subscriptionTier === 'pro' ? 'Pro' : subscriptionTier === 'basic' ? 'Basic' : 'Free';
    const tierDetail = subscriptionTier === 'pro' ? 'Unlimited checks' : subscriptionTier === 'basic' ? '50 checks/month' : '3 checks/month';

    switch (subscription.status) {
      case 'active':
        return { plan: tierName, detail: tierDetail, isActive: true };
      case 'past_due':
        return { plan: `${tierName} (Past Due)`, detail: 'Please update payment', isActive: true };
      case 'cancelled':
        return { plan: 'Cancelled', detail: 'Access until end of period', isActive: true };
      default:
        return { plan: 'Free', detail: '3 checks/month', isActive: false };
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="settings-tab-content">
            <h2>General</h2>
            <p className="settings-description">Manage your profile information</p>

            <form onSubmit={handleSave} className="settings-form">
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    id="first_name"
                    type="text"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    placeholder="Enter your first name"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    id="last_name"
                    type="text"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="practice_name">Practice Name</label>
                <input
                  id="practice_name"
                  type="text"
                  value={profile.practice_name}
                  onChange={(e) => setProfile({ ...profile, practice_name: e.target.value })}
                  placeholder="Enter your practice name"
                />
              </div>

              <div className="form-field">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={profile.role}
                  onChange={(e) => setProfile({ ...profile, role: e.target.value })}
                >
                  <option value="">Select your role</option>
                  <option value="Psychologist">Psychologist</option>
                  <option value="Practice Manager">Practice Manager</option>
                  <option value="Admin">Admin</option>
                  <option value="Reception">Reception</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        );

      case 'account':
        return (
          <div className="settings-tab-content">
            <h2>Account</h2>
            <p className="settings-description">Manage your account settings</p>

            <div className="settings-section">
              <h3>Email</h3>
              <div className="account-info-row">
                <span className="account-email">{user?.email}</span>
              </div>
            </div>

            <div className="settings-section">
              <h3>Password</h3>
              {!showPasswordForm ? (
                <button className="btn-secondary" onClick={() => setShowPasswordForm(true)}>
                  Change Password
                </button>
              ) : (
                <form onSubmit={handlePasswordChange} className="settings-form">
                  <div className="form-field">
                    <label htmlFor="new_password">New Password</label>
                    <input
                      id="new_password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="confirm_new_password">Confirm New Password</label>
                    <input
                      id="confirm_new_password"
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={passwordLoading}>
                      {passwordLoading ? 'Updating...' : 'Update Password'}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setNewPassword('');
                        setConfirmNewPassword('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="settings-section">
              <h3>Session</h3>
              <button className="btn-secondary" onClick={onLogout}>
                Log Out
              </button>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="settings-tab-content">
            <h2>Privacy</h2>
            <p className="settings-description">Manage your privacy preferences</p>

            <div className="settings-section">
              <h3>Data Usage</h3>
              <p className="privacy-info">
                Your compliance check data is processed securely and is not shared with third parties.
                We only retain the data necessary to provide you with our service.
              </p>
            </div>

            <div className="settings-section">
              <h3>Check History</h3>
              <p className="privacy-info">
                Your compliance check history is stored locally in your browser and is not synced to our servers.
              </p>
            </div>

            <div className="settings-section">
              <h3>Privacy Policy</h3>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                View Privacy Policy
              </a>
            </div>
          </div>
        );

      case 'billing':
        const formatRenewalDate = (dateString) => {
          if (!dateString) return null;
          const date = new Date(dateString);
          return date.toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        };

        return (
          <div className="settings-tab-content">
            <h2>Billing</h2>
            <p className="settings-description">Manage your subscription and billing</p>

            <div className="settings-section">
              <h3>Current Plan</h3>
              <div className={`plan-card ${getSubscriptionDisplay().isActive ? 'active' : ''}`}>
                <div className="plan-info">
                  <span className="plan-name">{getSubscriptionDisplay().plan}</span>
                  <span className="plan-detail">{getSubscriptionDisplay().detail}</span>
                  {subscription.periodEnd && getSubscriptionDisplay().isActive && (
                    <span className="plan-renewal">
                      {subscription.status === 'cancelled'
                        ? `Access until ${formatRenewalDate(subscription.periodEnd)}`
                        : `Renews on ${formatRenewalDate(subscription.periodEnd)}`
                      }
                    </span>
                  )}
                </div>
                {getSubscriptionDisplay().isActive ? (
                  <button
                    className="btn-secondary"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                  >
                    {portalLoading ? 'Loading...' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button className="btn-primary" onClick={() => navigate('/pricing')}>
                    View Plans
                  </button>
                )}
              </div>
            </div>

            <div className="settings-section">
              <h3>Monthly Usage</h3>
              <div className="usage-card">
                <div className="usage-header">
                  <span className="usage-label">Checks This Month</span>
                  <span className="usage-count">
                    {subscriptionTier === 'pro'
                      ? `${monthlyChecksUsed} used (unlimited)`
                      : `${monthlyChecksUsed} / ${monthlyCheckLimit}`
                    }
                  </span>
                </div>
                {subscriptionTier !== 'pro' && (
                  <div className="usage-bar">
                    <div
                      className="usage-fill"
                      style={{ width: `${Math.min((monthlyChecksUsed / monthlyCheckLimit) * 100, 100)}%` }}
                    />
                  </div>
                )}
                <p className="usage-note">
                  {checksResetDate
                    ? `Resets on ${formatRenewalDate(checksResetDate)}`
                    : subscriptionTier === 'pro'
                      ? 'Unlimited checks included with Pro'
                      : `${monthlyCheckLimit - monthlyChecksUsed} check${monthlyCheckLimit - monthlyChecksUsed !== 1 ? 's' : ''} remaining this month`
                  }
                </p>
              </div>
            </div>
          </div>
        );

      case 'delete':
        return (
          <div className="settings-tab-content">
            <h2>Delete Account</h2>
            <p className="settings-description danger-text">
              Permanently delete your account and all associated data
            </p>

            <div className="danger-zone">
              <div className="danger-warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <h3>Warning</h3>
                  <p>This action cannot be undone. All your data, including your profile and check history, will be permanently deleted.</p>
                </div>
              </div>

              {!showDeleteConfirm ? (
                <button className="btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                  Delete My Account
                </button>
              ) : (
                <div className="delete-confirm">
                  <p>Type <strong>DELETE</strong> to confirm:</p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                  />
                  <div className="form-actions">
                    <button className="btn-danger" onClick={handleDeleteAccount}>
                      Permanently Delete Account
                    </button>
                    <button
                      className="btn-ghost"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-page">
      {/* Settings Header */}
      <div className="settings-header">
        <div className="settings-header-left">
          {!sidebarOpen && (
            <button
              className="sidebar-toggle"
              onClick={onToggleSidebar}
              aria-label="Toggle sidebar"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          )}
          <h1>Settings</h1>
        </div>
        <button className="close-settings-btn" onClick={() => navigate('/checker')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="settings-message error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
          <button onClick={() => setError('')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {successMessage && (
        <div className="settings-message success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          {successMessage}
        </div>
      )}

      {/* Content */}
      <div className="settings-body">
        {/* Navigation */}
        <nav className="settings-nav">
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.id}
              className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'delete' ? 'danger' : ''}`}
              onClick={() => handleTabClick(tab)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className="settings-content">
          {loading ? (
            <div className="settings-loading">
              <div className="spinner"></div>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPageNew;
